import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { firstValueFrom, timeout } from 'rxjs';
import { InvoiceService } from '@app/core/services/InvoiceService';
import { OrganizationService } from '@app/core/services/OrganizationService';
import { EditCompanyDialogComponent } from '@app/shared/company/EditCompanyDialog.Component';

@Component({
  standalone: true,
  selector: 'proforma-invoice-dialog',
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule],
  templateUrl: './proforma-invoice-dialog.component.html',
  styleUrls: ['./proforma-invoice-dialog.component.scss']
})
export class ProformaInvoiceDialogComponent implements OnInit, OnDestroy {
  preview: any;
  pdfUrl: SafeResourceUrl;
  loading = true;
  sending = false;
  editingCustomer = false;
  error = '';
  private blobUrl: string;
  private destroyed = false;

  constructor(@Inject(MAT_DIALOG_DATA) public request: any,
    public dialogRef: MatDialogRef<ProformaInvoiceDialogComponent>,
    private invoiceService: InvoiceService, private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef, private dialog: MatDialog, private organizationService: OrganizationService) {}

  async ngOnInit() {
    await this.loadPreview();
  }

  async loadPreview() {
    this.loading = true;
    this.error = '';
    this.preview = undefined;
    this.pdfUrl = undefined;
    delete this.request.PreviewHash;
    delete this.request.PreviousDocumentGuid;
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    this.blobUrl = undefined;
    try {
      const preview = await firstValueFrom(this.invoiceService.previewProformaInvoice(this.request.Invoice.Id, this.request));
      if (this.destroyed) return;
      this.preview = preview;
      const bytes = Uint8Array.from(atob(preview.PdfBase64), character => character.charCodeAt(0));
      this.blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.blobUrl);
      this.request.PreviewHash = preview.PreviewHash;
      this.request.PreviousDocumentGuid = preview.PreviousDocumentGuid;
    } catch (error) {
      if (this.destroyed) return;
      this.error = error?.error?.Message || 'Kunde inte förhandsgranska självfakturan. Försök igen.';
    } finally {
      this.loading = false;
      // Flush the async result before Angular's development check compares the loading binding.
      if (!this.destroyed) this.cdr.detectChanges();
    }
  }

  downloadPdf() {
    if (this.loading || !this.blobUrl || !this.preview?.Number || this.destroyed) return;
    const link = document.createElement('a');
    link.href = this.blobUrl;
    link.download = `${this.preview.Number}.pdf`;
    link.click();
  }

  async editCustomer() {
    const organizationId = this.request.Invoice.RecipientOrganizationId;
    if (!organizationId || this.loading || this.sending || this.editingCustomer || this.destroyed) return;
    this.editingCustomer = true;
    this.dialogRef.disableClose = true;
    this.cdr.markForCheck();
    try {
      const organization = await firstValueFrom(this.organizationService.get(organizationId).pipe(timeout(15000)));
      if (this.destroyed) return;
      if (organization?.Id !== organizationId || !organization.Company?.Id) {
        throw new Error('Recipient company missing');
      }
      const result = await firstValueFrom(this.dialog.open(EditCompanyDialogComponent, {
        width: '640px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 48px)',
        panelClass: 'edit-company-dialog',
        data: { customer: organization.Company, organization }
      }).afterClosed());
      if (result && !this.destroyed) await this.loadPreview();
    } catch (error) {
      if (!this.destroyed) this.error = error?.error?.Message || 'Kunde inte öppna kunduppgifterna. Försök igen.';
    } finally {
      this.editingCustomer = false;
      this.dialogRef.disableClose = false;
      if (!this.destroyed) this.cdr.detectChanges();
    }
  }

  async approve() {
    if (this.sending || this.loading || this.editingCustomer || !this.preview || this.error) return;
    this.sending = true;
    this.dialogRef.disableClose = true;
    this.cdr.markForCheck();
    try {
      const result = await firstValueFrom(this.invoiceService.approveProformaInvoice(this.request.Invoice.Id, this.request));
      this.dialogRef.close(result);
    } catch (error) {
      this.error = error?.error?.Message || 'Kunde inte skapa självfakturan. Stäng och öppna dialogen igen innan du försöker på nytt.';
    } finally {
      this.sending = false; this.dialogRef.disableClose = false;
      if (!this.destroyed) this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
  }
}
